import { useEffect, useState } from 'react';
import {
  Button,
  Description,
  Drawer,
  FieldError,
  ListBox,
  Spinner,
  type OverlayTriggerState,
} from '@heroui/react';
import { AsyncButton } from '../../../components/ui/async-button';
import type { UpdateMembershipPayload } from '../../../api/memberships';
import type { UserMembership, UserWithMemberships } from '../../../api/users';
import { DrawerSelectField } from '../../../components/ui/drawer-form-field';
import { drawerBodyClassName, drawerFooterClassName, sideDrawerDialogClassName } from '../../../components/ui/side-drawer-classes';
import { getUserDisplayName } from '../utils/user-labels';

interface MembershipFormDrawerProps {
  state: OverlayTriggerState;
  membership: UserMembership | null;
  user: UserWithMemberships | null;
  onSubmit: (membershipId: string, payload: UpdateMembershipPayload) => Promise<void>;
}

export function MembershipFormDrawer({
  state,
  membership,
  user,
  onSubmit,
}: MembershipFormDrawerProps) {
  const [role, setRole] = useState<UserMembership['role']>('USER');
  const [status, setStatus] = useState<UserMembership['status']>('ACTIVE');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!state.isOpen || !membership) return;
    setRole(membership.role);
    setStatus(membership.status);
    setError(null);
    setSubmitting(false);
  }, [state.isOpen, membership]);

  const handleSubmit = async () => {
    if (!membership || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(membership.id, { role, status });
      state.close();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : typeof err === 'object' && err !== null && 'message' in err
            ? String((err as { message: unknown }).message)
            : 'Não foi possível salvar. Tente novamente.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const userLabel = user ? getUserDisplayName(user) : '';

  return (
    <Drawer state={state}>
      <Drawer.Backdrop isDismissable>
        <Drawer.Content placement="right">
          <Drawer.Dialog className={sideDrawerDialogClassName}>
            <Drawer.Header>
              <Drawer.Heading>Editar associação</Drawer.Heading>
              <Drawer.CloseTrigger />
            </Drawer.Header>
            <Drawer.Body className={drawerBodyClassName}>
              {membership && user ? (
                <Description>
                  {userLabel} · {membership.condominiumName}
                </Description>
              ) : null}

              <DrawerSelectField
                label="Papel"
                value={role}
                onChange={(value) => setRole(value as UserMembership['role'])}
                isDisabled={submitting}
              >
                <ListBox>
                  <ListBox.Item id="MANAGER" textValue="Gestor">
                    Gestor
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                  <ListBox.Item id="USER" textValue="Usuário">
                    Usuário
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                </ListBox>
              </DrawerSelectField>

              <DrawerSelectField
                label="Status"
                value={status}
                onChange={(value) => setStatus(value as UserMembership['status'])}
                isDisabled={submitting}
              >
                <ListBox>
                  <ListBox.Item id="ACTIVE" textValue="Ativo">
                    Ativo
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                  <ListBox.Item id="INVITED" textValue="Convidado">
                    Convidado
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                  <ListBox.Item id="SUSPENDED" textValue="Suspenso">
                    Suspenso
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                </ListBox>
              </DrawerSelectField>

              {error ? <FieldError>{error}</FieldError> : null}
            </Drawer.Body>
            <Drawer.Footer className={drawerFooterClassName}>
              <Button variant="ghost" isDisabled={submitting} onPress={state.close}>
                Cancelar
              </Button>
              <AsyncButton
                variant="primary"
                isPending={submitting}
                isDisabled={!membership}
                onPress={handleSubmit}
              >
                Salvar alterações
              </AsyncButton>
            </Drawer.Footer>
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
    </Drawer>
  );
}
